import {
  GS1_AI_DICTIONARY,
  isFixedLengthAi,
  validateGtinCheckDigit,
  type Gs1AiInfo,
  type RenderableSymbology,
} from '@tsagko/label-contracts'

/**
 * A translatable message produced by a pure helper: the key plus its
 * interpolation params, translated by the VIEW (`t(d.key, d.params)`).
 * Same contract as `BadgeLabel` in badge-state.ts — module-scope constants
 * and free functions can't call `t()`, so they hand back a descriptor.
 */
export interface HintDescriptor {
  key: string
  params?: Record<string, string | number>
}

// Symbology names are standards identifiers, not prose — never translated.
export const SYMBOLOGY_OPTIONS: { value: RenderableSymbology; label: string }[] = [
  { value: 'code-128', label: 'Code 128' },
  { value: 'ean-13', label: 'EAN-13' },
  { value: 'qr', label: 'QR' },
  { value: 'gs1-128', label: 'GS1-128' },
  { value: 'itf', label: 'ITF-14' },
]

const SUGGESTED: Record<string, string> = {
  '00': 'sscc', '01': 'gtin', '10': 'lot', '11': 'prodDate',
  '17': 'expiry', '21': 'serial', '30': 'count', '3103': 'netWeightKg',
}

export function suggestedVariableName(ai: string): string {
  return SUGGESTED[ai] ?? `ai${ai}`
}

export interface AiOption {
  ai: string // a concrete AI, or 'custom'
  labelKey: string
  suggestedVar: string
  info: Gs1AiInfo
}

// Module scope can't call t(): each AI carries its key, translated at render.
const AI_LABEL_KEYS: Record<string, string> = {
  '00': 'symbology.ai.ai00',
  '01': 'symbology.ai.ai01',
  '10': 'symbology.ai.ai10',
  '11': 'symbology.ai.ai11',
  '17': 'symbology.ai.ai17',
  '21': 'symbology.ai.ai21',
  '30': 'symbology.ai.ai30',
  '3103': 'symbology.ai.ai3103',
}

/** Dictionary entry for a known AI, or a synthesized default for a custom code. */
export function aiInfoFor(ai: string): Gs1AiInfo {
  return (
    GS1_AI_DICTIONARY[ai] ?? {
      ai,
      fixedLength: isFixedLengthAi(ai),
      checkDigit: 'none',
      defaultVarType: 'text',
    }
  )
}

export const AI_OPTIONS: AiOption[] = [
  ...Object.keys(AI_LABEL_KEYS).map(ai => ({
    ai,
    labelKey: AI_LABEL_KEYS[ai]!,
    suggestedVar: suggestedVariableName(ai),
    info: GS1_AI_DICTIONARY[ai]!,
  })),
  { ai: 'custom', labelKey: 'symbology.ai.custom', suggestedVar: '', info: aiInfoFor('custom') },
]

/**
 * Check-digit verdict for an all-digit GTIN/SSCC string, as a descriptor the
 * caller translates. Shared by the EAN-13 and GS1-AI editors, which show the
 * same wording.
 *
 * NOTE: on failure the interpolated `{{reason}}` is the English string
 * `validateGtinCheckDigit` returns from @tsagko/label-contracts — a package
 * outside this app with no i18n of its own. That tail stays English in every
 * locale; forking the contracts package to translate it is not worth it.
 */
export function checkDigitHint(digits: string): HintDescriptor {
  const v = validateGtinCheckDigit(digits)
  return v.ok
    ? { key: 'symbology.hint.checkDigitValid', params: { digit: digits.slice(-1) } }
    : { key: 'symbology.hint.checkDigitWrong', params: { reason: v.reason } }
}
